import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SIGNATURE_TERMS_EN, SIGNATURE_TERMS_HE, formResponseSchema, type IssuanceForm } from '@unit/shared';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { SignatureCanvas } from './SignatureCanvas.js';

const primary =
  'rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

/**
 * The sign-or-reject panel on a pending form, shown only to the soldier it
 * names (REQUIREMENTS §2.5 "Sign", §2.6). The soldier's own signed-in
 * session is all the server asks for (owner decision 2026-09-13: no step-up).
 */
export function SignPanel({ form, onChanged }: { form: IssuanceForm; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [image, setImage] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const sign = useAction(() =>
    apiPost(`/forms/${form.id}/sign`, formResponseSchema, {
      imageDataUrl: image,
      comment: comment.trim() || null,
    }),
  );
  const reject = useAction(() => apiPost(`/forms/${form.id}/reject`, formResponseSchema, { reason }));
  const onInk = useCallback((dataUrl: string | null) => setImage(dataUrl), []);

  const terms = i18n.language.startsWith('en') ? SIGNATURE_TERMS_EN : SIGNATURE_TERMS_HE;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="sign-panel">
      <h3 className="font-semibold text-slate-900">{t('sign.title')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('sign.reviewHint', { count: form.lineItems.length })}</p>

      {!rejecting && (
        <>
          <p className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="sign-terms">
            {terms}
          </p>
          <div className="mt-3">
            <SignatureCanvas onChange={onInk} disabled={sign.busy} />
          </div>
          <label className="mt-3 block text-sm text-slate-600">
            {t('sign.commentLabel')}
            <textarea
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('sign.commentPlaceholder')}
              data-testid="sign-comment"
            />
          </label>
          <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="sign-confirm" className="mt-1" />
            {t('sign.confirm')}
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void sign.run().then((r) => r && onChanged())}
              disabled={!image || !confirmed || sign.busy}
              className={primary}
              data-testid="sign-submit"
            >
              {sign.busy ? t('sign.signing') : t('sign.submit')}
            </button>
            <button type="button" onClick={() => setRejecting(true)} disabled={sign.busy} className={secondary} data-testid="sign-reject-open">
              {t('sign.rejectOpen')}
            </button>
          </div>
          <div className="mt-3"><ActionError error={sign.error} /></div>
        </>
      )}

      {rejecting && (
        <div className="mt-4 rounded-xl bg-red-50 p-3 ring-1 ring-red-200" data-testid="reject-panel">
          <p className="text-sm text-red-900">{t('sign.rejectHint')}</p>
          <input
            className="mt-2 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
            placeholder={t('sign.rejectReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reject-reason"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => void reject.run().then((r) => r && onChanged())}
              disabled={reason.trim().length < 3 || reject.busy}
              className="rounded-lg bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40"
              data-testid="reject-submit"
            >
              {t('sign.rejectSubmit')}
            </button>
            <button type="button" onClick={() => setRejecting(false)} className={secondary}>{t('org.cancel')}</button>
          </div>
          <div className="mt-2"><ActionError error={reject.error} /></div>
        </div>
      )}
    </section>
  );
}
